import { useMemo } from 'react';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { markAsSafe, dangerouslyCreateSafeString } from '@confluence/safe-string';

type UseEditorAnalyticsEventProps = {
	blueprintType?: string;
	contentId: string;
	contentType: string;
	embeddedConfluenceSource?: string;
	source: string;
	subject: string;
	version?: number;
	collabService?: string;
};

export type TriggerAnalyticsEventType = (
	eventName: string,
	extraData?: { [key: string]: any },
) => Promise<void>;

export const useEditorAnalyticsEvent = ({
	blueprintType,
	contentId,
	contentType,
	embeddedConfluenceSource,
	source,
	subject,
	version,
	collabService,
}: UseEditorAnalyticsEventProps) => {
	const triggerAnalyticsEvent = useMemo<TriggerAnalyticsEventType>(
		() =>
			async (eventName: string, extraData: { [key: string]: any } = {}) => {
				const analyticDetails = {
					contentId,
					contentType: markAsSafe('page', 'blogpost')(contentType),
					blueprintType: dangerouslyCreateSafeString(blueprintType),
					editorVersion: markAsSafe('fabric', 'tinymce')('fabric'),
					version: version || null,
					collabService,
					...(embeddedConfluenceSource && { embeddedConfluenceSource }),
					...extraData,
				};

				const client = await getAnalyticsWebClient();

				await client.sendTrackEvent({
					action: eventName,
					actionSubject: subject,
					source,
					objectType: contentType,
					objectId: contentId,
					attributes: analyticDetails,
				});
			},
		[
			blueprintType,
			contentId,
			contentType,
			embeddedConfluenceSource,
			source,
			subject,
			version,
			collabService,
		],
	);

	return {
		triggerAnalyticsEvent,
	};
};
